import { Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { Button } from "../components/ui/Button";
import { useConfirm } from "../components/ui/ConfirmDialog";
import { EmptyState, ErrorState, Skeleton } from "../components/ui/Feedback";
import { useToast } from "../components/ui/Toast";
import { Hint } from "../components/ui/Tooltip";
import {
	assetUrl,
	useDeleteMedia,
	useMediaLibrary,
	useUploadMedia,
} from "../features/media/useMedia";
import { cn } from "../lib/cn";
import { formatDate } from "../lib/format";
import { MAX_UPLOAD_BYTES } from "../lib/image";

const formatBytes = (bytes: number): string =>
	bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const LIBRARY_HELP = `Images are converted to WebP in your browser before upload and capped at ${
	MAX_UPLOAD_BYTES / 1024 / 1024
}MB. The same file uploaded twice is stored once.`;

export const ContentPage = () => {
	const library = useMediaLibrary();
	const upload = useUploadMedia();
	const remove = useDeleteMedia();
	const toast = useToast();
	const confirm = useConfirm();
	const fileInput = useRef<HTMLInputElement>(null);

	const handleFile = (file: File | undefined) => {
		if (!file) return;
		upload.mutate(file, {
			onSuccess: () => toast.push("Image uploaded"),
			onError: (error) => toast.push(error.message, "error"),
		});
	};

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h2 className="text-xl font-semibold tracking-tight text-ink">Content library</h2>
					<p className="mt-1 flex items-center gap-1.5 text-sm text-ink-muted">
						Images reusable across every campaign
						<Hint label="About the library">{LIBRARY_HELP}</Hint>
					</p>
				</div>
				<>
					<input
						ref={fileInput}
						type="file"
						accept="image/png,image/jpeg,image/webp,image/gif"
						className="hidden"
						onChange={(event) => {
							handleFile(event.target.files?.[0]);
							event.target.value = "";
						}}
					/>
					<Button disabled={upload.isPending} onClick={() => fileInput.current?.click()}>
						<Upload
							className={cn("h-4 w-4", upload.isPending && "animate-pulse")}
							strokeWidth={2}
						/>
						{upload.isPending ? "Converting…" : "Upload image"}
					</Button>
				</>
			</div>

			{library.isError ? (
				<ErrorState title="Could not load library" description={library.error.message} />
			) : null}

			{library.isPending ? <Skeleton className="h-64 w-full" /> : null}

			{library.data && library.data.items.length === 0 ? (
				<EmptyState
					title="No images yet"
					description="Upload artwork here, or from inside a campaign's Content step."
				/>
			) : null}

			{library.data && library.data.items.length > 0 ? (
				<div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
					{library.data.items.map((asset) => (
						<div
							key={asset.id}
							className="overflow-hidden rounded-md border border-line bg-surface"
						>
							<img
								src={assetUrl(asset.id)}
								alt={asset.filename ?? ""}
								loading="lazy"
								className="h-32 w-full bg-raised object-contain"
							/>
							<div className="flex items-start justify-between gap-2 border-t border-line px-3 py-2">
								<div className="min-w-0">
									<p className="truncate text-xs font-medium text-ink">
										{asset.filename ?? "Untitled"}
									</p>
									<p className="mt-0.5 text-xs text-ink-faint">
										{asset.width && asset.height ? `${asset.width}×${asset.height} · ` : ""}
										{formatBytes(asset.bytes)}
									</p>
									<p className="text-xs text-ink-faint">{formatDate(asset.createdAt)}</p>
								</div>
								<Button
									variant="ghost"
									size="sm"
									aria-label={`Delete ${asset.filename ?? "image"}`}
									onClick={() =>
										confirm({
											title: "Delete this image?",
											description:
												"If a campaign still uses it, the delete is blocked and you will be told which.",
											onConfirm: () =>
												remove.mutate(asset.id, {
													onSuccess: () => toast.push("Image deleted"),
													onError: (error) => toast.push(error.message, "error"),
												}),
										})
									}
								>
									<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
								</Button>
							</div>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
};
